import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PackageForm = ({ onPackageAdded }) => {
  const [formData, setFormData] = useState({
    recipient_name: '',
    tracking_number: '',
    carrier: '',
    phone: '',
    email: '',
    notes: ''
  });
  const [loading, setLoading] = useState(false);
  const [showCustomCarrier, setShowCustomCarrier] = useState(false);
  const [customCarrier, setCustomCarrier] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.recipient_name.trim()) {
      toast.error('Recipient name is required');
      return;
    }

    setLoading(true);
    try {
      const finalCarrier = formData.carrier === 'Custom' ? customCarrier.trim() : formData.carrier.trim();
      
      const response = await axios.post(`${API}/packages`, {
        recipient_name: formData.recipient_name.trim(),
        tracking_number: formData.tracking_number.trim() || null,
        carrier: finalCarrier || null,
        phone: formData.phone.trim() || null,
        email: formData.email.trim() || null,
        notes: formData.notes.trim() || null
      });
      
      onPackageAdded(response.data);
      
      // Reset form
      setFormData({
        recipient_name: '',
        tracking_number: '',
        carrier: '',
        phone: '',
        email: '',
        notes: ''
      });
      setShowCustomCarrier(false);
      setCustomCarrier('');
    } catch (error) {
      console.error('Error creating package:', error);
      toast.error('Failed to add package');
    } finally {
      setLoading(false);
    }
  };

  const carriers = ['DHL', 'FedEx', 'UPS', 'Mondial', 'Custom'];

  return (
    <div>      
      <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            <i className="fas fa-user mr-2"></i>
            Recipient Name *
          </label>
          <input
            type="text"
            className="form-input w-full"
            placeholder="Enter recipient name"
            value={formData.recipient_name}
            onChange={(e) => setFormData(prev => ({ ...prev, recipient_name: e.target.value }))}
            data-testid="recipient-name-input"
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            <i className="fas fa-barcode mr-2"></i>
            Tracking Number
          </label>
          <input
            type="text"
            className="form-input w-full font-mono"
            placeholder="Enter tracking number..."
            value={formData.tracking_number}
            onChange={(e) => setFormData(prev => ({ ...prev, tracking_number: e.target.value }))}
            data-testid="tracking-number-input"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            <i className="fas fa-truck mr-2"></i>
            Carrier
          </label>
          <select
            className="form-input w-full"
            value={formData.carrier}
            onChange={(e) => {
              const selectedCarrier = e.target.value;
              setFormData(prev => ({ ...prev, carrier: selectedCarrier }));
              setShowCustomCarrier(selectedCarrier === 'Custom');
            }}
            data-testid="carrier-select"
          >
            <option value="">Select carrier...</option>
            {carriers.map(carrier => (
              <option key={carrier} value={carrier}>{carrier}</option>
            ))}
          </select>
          
          {/* Custom Carrier Input */}
          {showCustomCarrier && (
            <div className="mt-2">
              <input
                type="text"
                className="form-input w-full"
                placeholder="Enter custom carrier name..."
                value={customCarrier}
                onChange={(e) => setCustomCarrier(e.target.value)}
                data-testid="custom-carrier-input"
              />
            </div>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            <i className="fas fa-phone mr-2"></i>
            Phone Number
          </label>
          <input
            type="tel"
            className="form-input w-full"
            placeholder="9-digit phone number (e.g., 612345678)..."
            value={formData.phone}
            onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
            data-testid="phone-input"
            maxLength="12"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            <i className="fas fa-envelope mr-2"></i>
            Email Address
          </label>
          <input
            type="email"
            className="form-input w-full"
            placeholder="Customer email address..."
            value={formData.email}
            onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
            data-testid="email-input"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            <i className="fas fa-sticky-note mr-2"></i>
            Notes
          </label>
          <textarea
            className="form-input w-full resize-none"
            rows={2}
            placeholder="Any special instructions or notes..."
            value={formData.notes}
            onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
            data-testid="notes-input"
          />
        </div>

        <div className="md:col-span-2 lg:col-span-1 flex items-end">
          <button
            type="submit"
            disabled={loading}
            className="btn-primary w-full py-3 font-semibold rounded-lg flex items-center justify-center space-x-2"
            data-testid="add-package-btn"
          >
            {loading ? (
              <>
                <div className="loading-spinner"></div>
                <span>Adding...</span>
              </>
            ) : (
              <>
                <i className="fas fa-plus"></i>
                <span>Add Package</span>
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default PackageForm;